import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Editor CRUD per i prodotti/garanzie associati ad ogni tipologia di polizza.
 * Endpoint: GET /api/policy-products/{policy_type_code} · POST / PUT / DELETE /api/policy-products
 */
export const PolicyProductsEditor = () => {
  const [policyTypes, setPolicyTypes] = useState([]);
  const [selectedTypeCode, setSelectedTypeCode] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", label: "", description: "", is_active: true, sort_order: 0 });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    api.get("/policy-types")
      .then(({ data }) => {
        const sorted = (data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setPolicyTypes(sorted);
        if (sorted.length > 0 && !selectedTypeCode) setSelectedTypeCode(sorted[0].code);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!selectedTypeCode) return;
    try { const { data } = await api.get(`/policy-products/${selectedTypeCode}`); setItems(data || []); }
    catch { /* silent */ }
  }, [selectedTypeCode]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!selectedTypeCode) { toast.error("Seleziona una tipologia di polizza"); return; }
    const code = (form.code || "").trim().toLowerCase().replace(/[^a-z0-9_-]/g, "_");
    if (!code) { toast.error("Codice obbligatorio"); return; }
    if (!form.label.trim()) { toast.error("Etichetta obbligatoria"); return; }
    const payload = {
      policy_type_code: selectedTypeCode,
      code,
      label: form.label.trim(),
      description: (form.description || "").trim() || null,
      is_active: !!form.is_active,
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (editingId) {
        await api.put(`/policy-products/${editingId}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/policy-products", payload);
        toast.success("Prodotto creato");
      }
      setForm({ code: "", label: "", description: "", is_active: true, sort_order: 0 });
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (it) => {
    setEditingId(it.id);
    setForm({ code: it.code, label: it.label, description: it.description || "", is_active: it.is_active !== false, sort_order: it.sort_order || 0 });
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it.label}"?`)) return;
    try { await api.delete(`/policy-products/${it.id}`); toast.success("Prodotto eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mt-6 p-5" data-testid="policy-products-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">🛡️ Prodotti / Garanzie per tipologia</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Definisci le garanzie disponibili per ogni tipologia di polizza. Verranno mostrate come selezione multipla nei preventivi/polizze.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-xs">Tipologia di polizza</Label>
        <Select value={selectedTypeCode} onValueChange={setSelectedTypeCode}>
          <SelectTrigger data-testid="policy-products-type-select" className="w-full sm:w-80">
            <SelectValue placeholder="Seleziona…" />
          </SelectTrigger>
          <SelectContent>
            {policyTypes.map((t) => (
              <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-3 p-3 bg-slate-50 rounded-md border border-slate-200">
        <div className="sm:col-span-3">
          <Label className="text-xs">Codice <span className="text-slate-400">(univoco)</span></Label>
          <Input
            data-testid="policy-product-form-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="es. rca"
            className="h-8 text-sm font-mono lowercase"
            disabled={!!editingId}
          />
        </div>
        <div className="sm:col-span-6">
          <Label className="text-xs">Etichetta (nome garanzia)</Label>
          <Input
            data-testid="policy-product-form-label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="es. RC Auto Obbligatoria"
            className="h-8 text-sm"
          />
        </div>
        <div className="sm:col-span-1">
          <Label className="text-xs">Ordine</Label>
          <Input
            data-testid="policy-product-form-order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            className="h-8 text-sm"
          />
        </div>
        <div className="sm:col-span-2 flex items-end gap-1">
          <Button
            type="button"
            size="sm"
            className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
            onClick={submit}
            data-testid="policy-product-form-submit"
            disabled={!selectedTypeCode}
          >
            {editingId ? "Salva" : "+ Aggiungi"}
          </Button>
          {editingId && (
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => { setEditingId(null); setForm({ code: "", label: "", description: "", is_active: true, sort_order: 0 }); }}>
              X
            </Button>
          )}
        </div>
        <div className="sm:col-span-12">
          <Label className="text-xs">Descrizione (opzionale)</Label>
          <Input
            data-testid="policy-product-form-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Breve descrizione mostrata nel preventivo"
            className="h-8 text-sm"
          />
        </div>
      </div>

      {!selectedTypeCode ? (
        <p className="text-xs text-slate-500 italic px-2 py-3">Seleziona una tipologia di polizza per gestire i suoi prodotti.</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500 italic px-2 py-3">
          Nessun prodotto configurato per <strong>{selectedTypeCode}</strong>. I default verranno seedati al primo accesso.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="policy-products-table">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <th className="text-left px-3 py-2">Ordine</th>
                <th className="text-left px-3 py-2">Codice</th>
                <th className="text-left px-3 py-2">Etichetta</th>
                <th className="text-left px-3 py-2">Descrizione</th>
                <th className="text-center px-3 py-2">Attivo</th>
                <th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`policy-product-row-${it.id}`}>
                  <td className="px-3 py-2 text-slate-500 tabular-nums">{it.sort_order ?? 0}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.code}</td>
                  <td className="px-3 py-2 font-medium">{it.label}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs max-w-xs truncate">{it.description || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {it.is_active !== false ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => edit(it)} className="text-xs text-blue-700 hover:underline mr-3" data-testid={`policy-product-edit-${it.id}`}>Modifica</button>
                    <button
                      onClick={() => {
                        // 🆕 Duplica: pre-compila il form con i dati attuali (codice con suffisso _copy)
                        setEditingId(null);
                        const baseCode = `${it.code}_copy`;
                        // Assicura unicità: se esiste già un _copy, aggiungi numero progressivo
                        let candidate = baseCode;
                        let n = 2;
                        const existing = new Set(items.map((x) => x.code));
                        while (existing.has(candidate)) {
                          candidate = `${baseCode}_${n++}`;
                        }
                        setForm({
                          code: candidate,
                          label: `${it.label} (copia)`,
                          description: it.description || "",
                          is_active: it.is_active !== false,
                          sort_order: (it.sort_order || 0) + 1,
                        });
                        toast.info("Form precompilato con i dati duplicati. Clicca '+ Aggiungi' per salvare la copia.");
                        try { document.querySelector('[data-testid="policy-product-form-code"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
                      }}
                      className="text-xs text-emerald-700 hover:underline mr-3"
                      data-testid={`policy-product-duplicate-${it.id}`}
                    >Duplica</button>
                    <button onClick={() => remove(it)} className="text-xs text-red-700 hover:underline" data-testid={`policy-product-delete-${it.id}`}>Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PolicyProductsEditor;
